import {
  Accordion,
  Box,
  Button,
  HStack,
  Skeleton,
  Text,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  date as commonDate,
  utils as commonUtils,
  finleyAssert,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { useGetLatestAssignmentReceivablesForLoanProductLazyQueryGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { AccordionItem } from './AccordionItem';
import { useLoanProductCreditFacilityGroupContext } from 'packages/loanProduct/hooks/LoanProductCreditFacilityGroupingProvider';
import {
  LoanProductforLpCFGroupingProvider,
  useLoanProductForLpCfGroupingContext,
} from 'packages/loanProduct/hooks/LoanProductForLpCfGroupingContext';
import { AssignmentTemplate } from 'packages/loanProduct/ui/AssignmentTemplate';
import { parseAssignmentReceivablesToTableDataAny } from 'packages/loanProduct/ui/utils';
import { LoanProductSelect } from 'packages/loanProduct/ui/v2/AssignByIndividual/LoanProductSelect';
import { ReceivableTable } from 'packages/loanProduct/ui/v2/AssignByIndividual/ReceivableTable';
import { CheckLatestPipelineRunStatus } from 'packages/loanProduct/ui/v2/CheckLatestPipelineRunStatus';

export const AssignByIndividualBody: React.FC<{
  fileVersion: number;
  masterLoanTapeIds: string[];
  organizationId: string;
  reportingDate: Util.FullDateStr;
}> = ({ reportingDate, masterLoanTapeIds }) => {
  const { lpToCfEntityGrouping } = useLoanProductCreditFacilityGroupContext();
  const { loanProduct } = useLoanProductForLpCfGroupingContext();

  const creditFacilitiesMap: Record<
    string,
    { id: string; slug: string; borrowerEntityName: string }
  > = useMemo(() => {
    const creditFacilities = _.filter(
      lpToCfEntityGrouping.creditFacilities,
      ({ id }) => _.includes(loanProduct.currentCreditFacilities, id),
    );
    return _.keyBy(creditFacilities, 'id');
  }, [lpToCfEntityGrouping, loanProduct]);

  const [
    proposedAssignedReceivables,
    setProposedAssignedReceivables,
  ] = useState<{
    [loanProductId: string]: {
      // @jendevelops TODO make this AssignmentReceivable[]
      [creditFacilityId: string]: any[];
    };
  } | null>(null);

  const [leftOnBalanceSheet, setLeftOnBalanceSheet] = useState<{
    [loanProductId: string]: any[];
  } | null>(null);

  const [
    getLatestAssignmentReceivables,
  ] = useGetLatestAssignmentReceivablesForLoanProductLazyQueryGQLType({
    onCompleted: (data) => {
      const { latestAssignmentReceivables } = data;
      finleyAssert(
        latestAssignmentReceivables,
        'Expected to have latest assignment receivables',
      );
      try {
        // @jendevelops TODO: validate JSON data
        const json = JSON.parse(latestAssignmentReceivables);
        const {
          creditFacilityId: fetchedCreditFacilityId,
          loanProductId: fetchedLoanProductId,
          type,
          receivables,
        } = json;
        if (type === 'proposedAssignmentReceivables') {
          setProposedAssignedReceivables((previousProposed) => {
            if (previousProposed === null) {
              return {
                [fetchedLoanProductId]: {
                  [fetchedCreditFacilityId]: receivables,
                },
              };
            }
            if (!(fetchedLoanProductId in previousProposed)) {
              return {
                ...previousProposed,
                [fetchedLoanProductId]: {
                  [fetchedCreditFacilityId]: receivables,
                },
              };
            }

            return {
              ...previousProposed,
              [fetchedLoanProductId]: {
                ...previousProposed[fetchedLoanProductId],
                [fetchedCreditFacilityId]: receivables,
              },
            };
          });
        }
        if (type === 'unassignedAssignmentReceivables') {
          setLeftOnBalanceSheet((previousProposed) => {
            if (previousProposed === null) {
              return {
                [fetchedLoanProductId]: receivables,
              };
            }

            return {
              ...previousProposed,
              [fetchedLoanProductId]: {
                [fetchedCreditFacilityId]: receivables,
              },
            };
          });
        }
      } catch (err) {
        if (err instanceof Error) {
          console.log(err.message);
        }
      }
    },
  });

  return (
    <AssignmentTemplate headerTitle="Assign By Receiveable">
      <HStack w="100%" spacing="40px">
        <LoanProductSelect inputFloatingLabelBackgroundColor="white" />
        <HStack spacing="16px">
          {/* @jendevelops TODO: add functionality */}
          <Button variant="outline" colorScheme="aurora">
            Add Column
          </Button>

          {/* @jendevelops TODO: add functionality */}
          <Button variant="solid" colorScheme="aurora">
            Update Propsed Owners
          </Button>
        </HStack>
      </HStack>

      <Text
        color="grey"
        p="16px 0"
      >{`Reporting Date: ${commonDate.fullDateStrToHumanDateString(
        reportingDate,
      )}`}</Text>

      <Accordion allowMultiple>
        {/* @jendevelops TODO how to get count before lazy fetch? Get from summary? From file name? */}
        {_.map(creditFacilitiesMap, (creditFacility) => {
          const data =
            proposedAssignedReceivables?.[loanProduct.id]?.[
              creditFacility.id
            ] ?? [];
          return (
            <AccordionItem
              key={loanProduct.id + creditFacility.id}
              title={`${commonUtils.creditFacilitySlugToName(
                creditFacility.slug,
              )} (${data.length})`}
              onClick={() => {
                if (
                  !proposedAssignedReceivables?.[loanProduct.id]?.[
                    creditFacility.id
                  ]
                ) {
                  getLatestAssignmentReceivables({
                    variables: {
                      type: 'proposedAssignmentReceivables',
                      loanProductId: loanProduct.id,
                      creditFacilityId: creditFacility.id,
                      masterLoanTapeIds,
                    },
                  });
                }
              }}
              panel={
                <ReceivableTable
                  data={parseAssignmentReceivablesToTableDataAny(data)}
                  columns={[]}
                  creditFacilitiesMap={creditFacilitiesMap}
                />
              }
            />
          );
        })}
        {/* @jendevelops TODO lazy fetch left on balance sheet */}
        <AccordionItem
          title={`Left on Balance Sheet ${
            leftOnBalanceSheet?.[loanProduct.id]?.length
              ? `(${leftOnBalanceSheet?.[loanProduct.id]?.length})`
              : ''
          }`}
          onClick={() => {
            if (!leftOnBalanceSheet?.[loanProduct.id]) {
              getLatestAssignmentReceivables({
                variables: {
                  type: 'unassignedAssignmentReceivables',
                  loanProductId: loanProduct.id,
                  // cfId doesn't matter here as lobs are aggregated across all loan tapes and are saved under each CF
                  creditFacilityId: lpToCfEntityGrouping.creditFacilities[0].id,
                  masterLoanTapeIds,
                },
              });
            }
          }}
          panel={
            <ReceivableTable
              data={parseAssignmentReceivablesToTableDataAny(
                leftOnBalanceSheet?.[loanProduct.id] || [],
              )}
              columns={[]}
              creditFacilitiesMap={creditFacilitiesMap}
            />
          }
        />
      </Accordion>
    </AssignmentTemplate>
  );
};

export const AssignByIndividual = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const history = useHistory();

  return (
    <Box w="100%">
      <CheckLatestPipelineRunStatus>
        {({ FallbackAssignmentTemplate, metadata }) => {
          if (metadata?.status !== 'success') {
            history.push('/assignments');
          }
          return metadata ? (
            <LoanProductforLpCFGroupingProvider renderLoading={<Skeleton />}>
              <AssignByIndividualBody
                masterLoanTapeIds={metadata.masterLoanTapeIds}
                reportingDate={metadata.reportingDate}
                fileVersion={metadata.fileVersion}
                organizationId={organizationId}
              />
            </LoanProductforLpCFGroupingProvider>
          ) : (
            <FallbackAssignmentTemplate />
          );
        }}
      </CheckLatestPipelineRunStatus>
    </Box>
  );
};
